import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { Locale } from '@/types/regionTypes';
import { getActionAriaLabel } from '@/utils/urlUtils';

import { useCompoundContext } from '@/components/Header/context';
import type { CountryCode, LanguageCodeEnum } from '@/graphql/saleor/api';
import { useContactInfo } from '@/hooks/useContactInfo';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsMdUp } from '@/hooks/useMedia';
import { useNativeCall } from '@/hooks/useNativeCall';
import { useRouter } from '@/hooks/useRouter';
import { EVENT_NAME } from '@/lib/gtm/const';
import { DEFAULT_LANGUAGE } from '@/lib/regions';
import { useRegionProvider } from '@/providers/RegionProvider';
import { countrySelectorActions } from '@/states/countrySelectorState';
import { searchStateActions } from '@/states/searchState';
import type { HeaderThemeKeys } from '@/theme/types/components';

import { preloadSearchOverlay } from '../../lazyOverlays';

import * as S from './HeaderInfo.styles';

export interface HeaderInfoProps {
  headerBackground: HeaderThemeKeys;
  hideLanguageSwitcher?: boolean;
  hidePhone?: boolean;
  hideSearch?: boolean;
  isMobileFooter?: boolean;
  isMobileMenuOpen: boolean;
  isOpenMobile?: boolean;
  isShrunk?: boolean;
  onMobileMenuHandler: (isOpen: boolean) => void;
  onShoppingBagDrawerHandler: Dispatch<SetStateAction<boolean>>;
  textColorOption?: string;
}

export const HeaderInfo = ({
  headerBackground,
  hideLanguageSwitcher = false,
  hidePhone = false,
  hideSearch = false,
  isMobileFooter = false,
  isMobileMenuOpen,
  isOpenMobile = false,
  isShrunk = true,
  onMobileMenuHandler,
  onShoppingBagDrawerHandler,
  textColorOption = 'dark',
}: HeaderInfoProps) => {
  const { changeCountry, country, locale } = useRegionProvider();
  const { asPath } = useRouter();
  useCompoundContext();

  const defaultLanguage = DEFAULT_LANGUAGE;
  const [isShowLanguageMenu, setIsShowLanguageMenu] = useState<boolean>(false);
  const languageRef = useRef<HTMLDivElement | null>(null);
  const [countryTabOpen, setCountryTabOpen] = useState(false);
  const isMdUp = useIsMdUp();

  const contactInfo = useContactInfo();
  const { sendEvent } = useGoogleAnalytics();
  const { initiateNativeCall } = useNativeCall(country.code);
  const callCustomerService = () => {
    sendEvent({
      event: EVENT_NAME.CONTACT,
      contact_type: 'phone',
    });
    initiateNativeCall();
  };
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        languageRef.current &&
        !languageRef.current.contains(e.target as Node)
      ) {
        setIsShowLanguageMenu(false);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => document.removeEventListener('click', handleClick, true);
  }, [languageRef]);

  const handleShoppingBagDrawerAutoClose = () => {
    if (!isMdUp) {
      onShoppingBagDrawerHandler(false);
    }
  };

  const handleSearchClick = () => {
    if (asPath?.includes('/search/')) {
      return;
    }
    if (isMobileMenuOpen) {
      openHandleMobileMenu(false);
    }
    handleShoppingBagDrawerAutoClose();
    searchStateActions.toggleOverlayStatus();
  };

  const openChangeLocation = () => {
    countrySelectorActions.changeOverlayStatus('OPEN');
    if (isMobileFooter) openHandleMobileMenu(false);
    handleShoppingBagDrawerAutoClose();
    setCountryTabOpen(true);
  };

  const onChangeLanguage = async (language: Locale) => {
    await changeCountry(
      country.slug as CountryCode,
      language.slug as LanguageCodeEnum,
    );
    openHandleMobileMenu(false);
  };

  const openHandleMobileMenu = (isOpen: boolean) => {
    onMobileMenuHandler(isOpen);
  };

  useEffect(() => {
    if (countryTabOpen) {
      if (document.body.style.position === 'fixed') {
        const observer = new MutationObserver(() => {
          if (document.body.style.position !== 'fixed') {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth',
            });
            observer.disconnect();
          }
        });
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['style'],
        });
        setTimeout(() => observer.disconnect(), 1000);
      } else {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        });
      }
      setCountryTabOpen(false);
    }
  }, [countryTabOpen, setCountryTabOpen]);

  return (
    <>
      <S.DesktopNav
        $headerBackground={headerBackground}
        $isMobileFooter={isMobileFooter}
      >
        {!hideLanguageSwitcher && (
          <li data-test-id='HeaderInfoDesktopNav-0'>
            <S.FlexBox
              className='selectOptions'
              onClick={() => openChangeLocation()}
            >
              <S.InternationalIcon
                $headerBackground={headerBackground}
                $isShrunk={isShrunk}
                $textColorOption={textColorOption || 'dark'}
                className='internationalIcon'
                name='international'
              />
              <S.Text className='region'>
                &nbsp; {`${country.code}`} &nbsp;
              </S.Text>
            </S.FlexBox>
            <S.SelectLanguage
              ref={languageRef}
              $headerBackground={headerBackground}
              $isActive={isShowLanguageMenu}
              className='selectOptions'
              data-testid='HeaderInfoSelectLanguage'
              onClick={() => setIsShowLanguageMenu((prevous) => !prevous)}
            >
              <S.Text className='language'>
                &nbsp; {`${locale.name}`} &nbsp;
              </S.Text>
              {isShowLanguageMenu ? (
                <S.ArrowUpIcon
                  $headerBackground={headerBackground}
                  $isShrunk={isShrunk}
                  $textColorOption={textColorOption || 'dark'}
                  className='arrowIcon'
                  name='arrowup'
                />
              ) : (
                <S.ArrowDownIcon
                  $headerBackground={headerBackground}
                  $isShrunk={isShrunk}
                  $textColorOption={textColorOption || 'dark'}
                  className='arrowIcon'
                  name='arrowdown'
                />
              )}
              {isShowLanguageMenu && (
                <S.LanguageBox
                  $isMobileFooter={isMobileFooter}
                  data-testid='HeaderInfoLanguageBox'
                >
                  <ul>
                    {(country.locales ?? [defaultLanguage])?.map(
                      (lang, index) => (
                        <li
                          key={'languageItem_' + lang.code}
                          data-test-id={`HeaderInfoLanguageItem-${index}`}
                        >
                          <S.FlexBox onClick={() => onChangeLanguage(lang)}>
                            <S.LanguageItem
                              $isActive={lang.slug === locale?.slug}
                              data-test-id={`HeaderInfoLanguageItemText-${index}`}
                            >
                              {lang.name}
                            </S.LanguageItem>
                            {lang.slug === locale?.slug && (
                              <S.CheckBoxIcon
                                onClick={(event) => {
                                  event.preventDefault();
                                }}
                              />
                            )}
                          </S.FlexBox>
                        </li>
                      ),
                    )}
                  </ul>
                </S.LanguageBox>
              )}
            </S.SelectLanguage>
          </li>
        )}
        {contactInfo.phoneNumber && !hidePhone && (
          <li data-test-id='HeaderInfoDesktopNav-1'>
            <S.FlexBox onClick={() => callCustomerService()}>
              <S.PhoneIcon
                $headerBackground={headerBackground}
                $isShrunk={isShrunk}
                $textColorOption={textColorOption || 'dark'}
                name='phone'
              />
              <S.Text>&nbsp; {contactInfo.phoneNumber}</S.Text>
            </S.FlexBox>
          </li>
        )}
      </S.DesktopNav>
      <S.MobileNav $isMobileFooter={isMobileFooter}>
        {isOpenMobile ? (
          <li data-test-id='HeaderInfoMobileNav-0'>
            <S.MobileIconButton
              aria-controls='mobile-menu'
              aria-expanded={isOpenMobile}
              aria-label={
                getActionAriaLabel('close_menu', locale.code) || 'Close menu'
              }
              data-testid='HeaderInfoMobileCloseMenuButton'
              onClick={() => openHandleMobileMenu(false)}
              type='button'
            >
              <S.CloseIcon
                $headerBackground={headerBackground}
                $isShrunk={isShrunk}
                $textColorOption={textColorOption || 'dark'}
                aria-hidden='true'
                focusable={false}
                name='close'
              />
            </S.MobileIconButton>
          </li>
        ) : (
          <li data-test-id='HeaderInfoMobileNav-1'>
            <S.MobileIconButton
              aria-controls='mobile-menu'
              aria-expanded={isOpenMobile}
              aria-label={
                getActionAriaLabel('open_menu', locale.code) || 'Open menu'
              }
              data-testid='HeaderInfoMobileOpenMenuButton'
              onClick={() => openHandleMobileMenu(true)}
              type='button'
            >
              <S.HamburgerIcon
                $headerBackground={headerBackground}
                $isShrunk={isShrunk}
                $textColorOption={textColorOption || 'dark'}
                aria-hidden='true'
                focusable={false}
                name='hamburger'
              />
            </S.MobileIconButton>
          </li>
        )}
        {!hideSearch && (
          <li data-test-id='HeaderInfoMobileNav-2'>
            <S.MobileIconButton
              aria-label={
                getActionAriaLabel('open_search', locale.code) || 'Search'
              }
              data-testid='HeaderInfoMobileSearchButton'
              onClick={handleSearchClick}
              onFocus={preloadSearchOverlay}
              onPointerEnter={preloadSearchOverlay}
              type='button'
            >
              <S.SearchIcon
                $headerBackground={headerBackground}
                $isShrunk={isShrunk}
                $textColorOption={textColorOption || 'dark'}
                aria-hidden='true'
                focusable={false}
                name='search'
              />
            </S.MobileIconButton>
          </li>
        )}
      </S.MobileNav>
    </>
  );
};
